import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  BookOpen, Sparkles, Calendar, Globe, HardDrive, FileText,
  Play, ChevronDown, ChevronRight, ArrowLeft, Search, Settings,
  LayoutTemplate, CreditCard, Share2, Download
} from 'lucide-react';

const GUIDE_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Sparkles,
    color: 'bg-blue-50 text-blue-600',
    steps: [
      { title: 'Create Your Account', description: 'Sign up with your email. You get a free 3-day Pro trial — no credit card needed. Explore all features risk-free.' },
      { title: 'Choose Your Content Type', description: 'From the dashboard, select what you want to create: Bible Study, Devotional, Curriculum, or Sermon Notes.' },
      { title: 'Fill in the Details', description: 'Enter your series title, scriptures, audience, tone, and any focus areas. The more specific you are, the better the output.' },
      { title: 'Generate & Edit', description: 'Click Generate and wait for your AI-crafted content. You can edit the output directly in the app before saving or exporting.' },
    ],
  },
  {
    id: 'bible-study',
    title: 'Creating a Bible Study',
    icon: BookOpen,
    color: 'bg-amber-50 text-amber-600',
    steps: [
      { title: 'Choose Template or Start Fresh', description: 'Browse pre-built seasonal templates (Advent, Easter, Lent, etc.) or start with a blank slate.' },
      { title: 'Set Your Parameters', description: 'Enter the series title, key scriptures, number of weeks (1-12), audience type, and tone. Optional: add denomination and focus areas.' },
      { title: 'Week-by-Week Generation', description: 'The AI generates each week individually for maximum quality. You\'ll see progress as each week completes.' },
      { title: 'Export & Share', description: 'Save to your library, export as PDF/Word/PPTX, or share directly via email with a beautiful branded link.' },
    ],
    videoPlaceholder: true,
  },
  {
    id: 'compare-religions',
    title: 'Comparing Religions',
    icon: Globe,
    color: 'bg-purple-50 text-purple-600',
    steps: [
      { title: 'Enter Your Thought', description: 'Type any theological concept, belief, or practice you want to compare across world religions.' },
      { title: 'Select Religions', description: 'Choose which religions to include: Islam, Judaism, Buddhism, Hinduism, Sikhism, Taoism, and more.' },
      { title: 'View the Comparison', description: 'Get a structured analysis with each religion\'s perspective, key texts, and a summary comparison table.' },
      { title: 'Save or Export', description: 'Save the comparison to your library or export as a formatted PDF for teaching or personal study.' },
    ],
    videoPlaceholder: true,
  },
  {
    id: 'scheduling',
    title: 'Church Scheduling',
    icon: Calendar,
    color: 'bg-green-50 text-green-600',
    steps: [
      { title: 'Choose Service Type', description: 'Select from 7 preset types: Sunday Service, Bible Study, Small Group, Prayer Meeting, Youth, Midweek, or Special Event.' },
      { title: 'Add Members', description: 'Build your team roster. Members appear as suggestions when assigning roles in schedule entries.' },
      { title: 'Create Entries', description: 'Fill in who\'s doing what: opening prayer, worship, preacher, reader, etc. Add custom fields as needed.' },
      { title: 'Customize & Reorder Fields', description: 'Drag to reorder fields, hide what you don\'t need, and save as reusable templates for future weeks.' },
      { title: 'Upload Church Logo', description: 'Upload your church logo — it appears on exported schedules alongside the creator name and branding.' },
      { title: 'Export', description: 'Export as PDF or Word document. Your schedule includes the church logo, creator name, and dsashun branding.' },
    ],
    videoPlaceholder: true,
  },
  {
    id: 'scripture-lookup',
    title: 'Scripture Lookup & Word Study',
    icon: Search,
    color: 'bg-teal-50 text-teal-600',
    steps: [
      { title: 'Look Up Scripture', description: 'Enter any Bible reference (e.g., "John 3:16" or "Psalm 23") to get the full text in multiple translations.' },
      { title: 'Word Study', description: 'Enter a Greek or Hebrew word to get etymology, Strong\'s number, usage in Scripture, and theological significance.' },
    ],
    videoPlaceholder: true,
  },
  {
    id: 'document-storage',
    title: 'Document Storage',
    icon: HardDrive,
    color: 'bg-orange-50 text-orange-600',
    steps: [
      { title: 'Create Folders', description: 'Organize your documents into folders with custom colors. Create sub-folders for detailed organization.' },
      { title: 'Upload Documents', description: 'Upload PDFs, Word docs, images, spreadsheets, and more (up to 25MB per file). Tag them for easy search.' },
      { title: 'Save Generated Content', description: 'Any content you generate can be saved directly to your document storage from the library.' },
      { title: 'Share Documents', description: 'Share within your organization or create public links for external sharing.' },
    ],
    videoPlaceholder: true,
  },
  {
    id: 'export-options',
    title: 'Exporting Your Content',
    icon: Download,
    color: 'bg-indigo-50 text-indigo-600',
    steps: [
      { title: 'PDF Export', description: 'Clean, print-ready PDF with your church branding. Great for handouts and bulletin inserts.' },
      { title: 'Word Document', description: 'Editable .docx format for further customization in Microsoft Word or Google Docs.' },
      { title: 'PowerPoint (PPTX)', description: 'Presentation slides with a professional navy & gold theme. Perfect for projection during services.' },
      { title: 'Email Sharing', description: 'Send studies directly to anyone via email with a beautiful branded preview.' },
    ],
  },
];

function GuideSection({ section, isOpen, onToggle, videos = [], isAdmin, onRemoveVideo, getVideoEmbed }) {
  const Icon = section.icon;
  const sectionVideos = videos.filter(v => v.section_id === section.id);
  return (
    <div className="border border-[#E2DFD8] rounded-xl overflow-hidden" data-testid={`guide-section-${section.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-6 py-5 text-left hover:bg-[#FAFAF8] transition-colors"
      >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${section.color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="flex-1 text-base font-semibold text-[#0F1A2A]">{section.title}</span>
        {isOpen ? <ChevronDown className="w-5 h-5 text-[#94A3B8]" /> : <ChevronRight className="w-5 h-5 text-[#94A3B8]" />}
      </button>
      {isOpen && (
        <div className="px-6 pb-6 space-y-4">
          {section.steps.map((step, idx) => (
            <div key={idx} className="flex gap-4">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1B3A6B] text-white text-xs font-bold flex items-center justify-center mt-0.5">
                {idx + 1}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-[#0F1A2A]">{step.title}</h4>
                <p className="text-sm text-[#475569] mt-0.5 leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
          {/* Video embeds */}
          {sectionVideos.length > 0 ? sectionVideos.map(v => (
            <div key={v.id} className="mt-4 rounded-xl overflow-hidden border border-[#E2DFD8]" data-testid={`video-${v.id}`}>
              <div className="aspect-video bg-black">
                <iframe
                  src={getVideoEmbed(v.url)}
                  title={v.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="p-3 bg-white flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#0F1A2A]">{v.title}</p>
                  {v.description && <p className="text-xs text-[#94A3B8] mt-0.5">{v.description}</p>}
                </div>
                {isAdmin && (
                  <button onClick={() => onRemoveVideo(v.id)} className="text-xs text-red-500 hover:underline">Remove</button>
                )}
              </div>
            </div>
          )) : section.videoPlaceholder ? (
            <div className="mt-4 rounded-xl border-2 border-dashed border-[#E2DFD8] bg-[#FAFAF8] p-8 flex flex-col items-center justify-center" data-testid={`video-placeholder-${section.id}`}>
              <div className="w-16 h-16 rounded-full bg-[#1B3A6B]/10 flex items-center justify-center mb-3">
                <Play className="w-7 h-7 text-[#1B3A6B] ml-0.5" />
              </div>
              <p className="text-sm font-medium text-[#475569]">Video Tutorial Coming Soon</p>
              <p className="text-xs text-[#94A3B8] mt-1">A walkthrough video for {section.title.toLowerCase()} will be added here</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function GuidePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openSections, setOpenSections] = useState(['getting-started']);
  const [videos, setVideos] = useState([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoSection, setNewVideoSection] = useState('');
  const [newVideoDesc, setNewVideoDesc] = useState('');

  const isAdmin = user?.role === 'platform_admin' || user?.role === 'admin';

  useEffect(() => {
    axios.get(`${API}/guide/content`).then(res => {
      setVideos(res.data.videos || []);
    }).catch(() => {});
  }, []);

  const toggleSection = (id) => {
    setOpenSections(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const addVideo = async () => {
    if (!newVideoTitle.trim() || !newVideoUrl.trim()) return;
    try {
      const res = await axios.post(`${API}/admin/guide/video`, {
        title: newVideoTitle.trim(),
        url: newVideoUrl.trim(),
        section_id: newVideoSection,
        description: newVideoDesc.trim(),
      });
      setVideos([...videos, res.data.video]);
      setNewVideoTitle('');
      setNewVideoUrl('');
      setNewVideoSection('');
      setNewVideoDesc('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add video');
    }
  };

  const removeVideo = async (id) => {
    try {
      await axios.delete(`${API}/admin/guide/video/${id}`);
      setVideos(videos.filter(v => v.id !== id));
    } catch (err) {
      alert('Failed to remove video');
    }
  };

  const getVideoEmbed = (url) => {
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    // Vimeo
    const vimMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimMatch) return `https://player.vimeo.com/video/${vimMatch[1]}`;
    return url;
  };

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2DFD8] sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <button onClick={() => user ? navigate('/app') : navigate('/')} className="p-2 hover:bg-[#F4F2EC] rounded-lg" data-testid="guide-back">
            <ArrowLeft className="w-5 h-5 text-[#475569]" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-[#1B3A6B]">Guide & How-To</h1>
            <p className="text-xs text-[#94A3B8]">Learn how to get the most out of dsashun</p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-4" data-testid="guide-page">
        {/* Quick links */}
        <div className="flex flex-wrap gap-2 mb-6">
          {GUIDE_SECTIONS.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => { if (!openSections.includes(s.id)) toggleSection(s.id); document.querySelector(`[data-testid="guide-section-${s.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#475569] border border-[#E2DFD8] rounded-full hover:border-[#1B3A6B] hover:text-[#1B3A6B] transition-colors"
              >
                <Icon className="w-3.5 h-3.5" /> {s.title}
              </button>
            );
          })}
        </div>

        {/* Admin Panel - Video Management */}
        {isAdmin && (
          <div className="mb-6">
            <button
              onClick={() => setShowAdminPanel(!showAdminPanel)}
              className="flex items-center gap-2 text-sm font-medium text-[#B8860B] hover:text-[#D19C11]"
              data-testid="guide-admin-toggle"
            >
              <Settings className="w-4 h-4" /> {showAdminPanel ? 'Hide Admin Panel' : 'Manage Content (Admin)'}
            </button>
            {showAdminPanel && (
              <div className="mt-3 p-4 bg-white rounded-xl border border-[#B8860B]/20 shadow-sm" data-testid="guide-admin-panel">
                <h4 className="text-sm font-semibold text-[#0F1A2A] mb-3">Add Video to Section</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                  <input
                    value={newVideoTitle}
                    onChange={e => setNewVideoTitle(e.target.value)}
                    placeholder="Video title"
                    className="px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg"
                    data-testid="guide-video-title"
                  />
                  <select
                    value={newVideoSection}
                    onChange={e => setNewVideoSection(e.target.value)}
                    className="px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg"
                    data-testid="guide-video-section"
                  >
                    <option value="">Select section...</option>
                    {GUIDE_SECTIONS.map(s => (
                      <option key={s.id} value={s.id}>{s.title}</option>
                    ))}
                  </select>
                </div>
                <input
                  value={newVideoUrl}
                  onChange={e => setNewVideoUrl(e.target.value)}
                  placeholder="Video URL (YouTube, Vimeo, or direct link)"
                  className="w-full px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg mb-2"
                  data-testid="guide-video-url"
                />
                <input
                  value={newVideoDesc}
                  onChange={e => setNewVideoDesc(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-2 text-sm border border-[#E2DFD8] rounded-lg mb-2"
                  data-testid="guide-video-desc"
                />
                <button
                  onClick={addVideo}
                  disabled={!newVideoTitle.trim() || !newVideoUrl.trim()}
                  className="px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-[#254B8A]"
                  data-testid="guide-add-video-btn"
                >
                  Add Video
                </button>
              </div>
            )}
          </div>
        )}

        {/* Sections */}
        {GUIDE_SECTIONS.map(section => (
          <GuideSection
            key={section.id}
            section={section}
            isOpen={openSections.includes(section.id)}
            onToggle={() => toggleSection(section.id)}
            videos={videos}
            isAdmin={isAdmin}
            onRemoveVideo={removeVideo}
            getVideoEmbed={getVideoEmbed}
          />
        ))}

        {/* FAQ */}
        <div className="mt-8 bg-white rounded-xl border border-[#E2DFD8] p-6" data-testid="guide-faq">
          <h3 className="text-base font-semibold text-[#0F1A2A] mb-4">Frequently Asked Questions</h3>
          <div className="space-y-4 text-sm">
            <div>
              <p className="font-medium text-[#1B3A6B]">Do I need a credit card to start?</p>
              <p className="text-[#475569] mt-1">No. Your 3-day free trial starts automatically when you sign up. No payment info needed until you're ready to upgrade.</p>
            </div>
            <div>
              <p className="font-medium text-[#1B3A6B]">How long does generation take?</p>
              <p className="text-[#475569] mt-1">Single-week content generates in 10-30 seconds. Multi-week series take longer since each week is generated individually for quality. You'll see progress updates.</p>
            </div>
            <div>
              <p className="font-medium text-[#1B3A6B]">Can I edit AI-generated content?</p>
              <p className="text-[#475569] mt-1">Yes! All generated content is fully editable. Click the edit icon in your library to make changes before exporting.</p>
            </div>
            <div>
              <p className="font-medium text-[#1B3A6B]">What languages are supported?</p>
              <p className="text-[#475569] mt-1">Content can be generated in English, Spanish, French, Portuguese, German, Korean, and Chinese.</p>
            </div>
            <div>
              <p className="font-medium text-[#1B3A6B]">How do I upload tutorial videos?</p>
              <p className="text-[#475569] mt-1">Admins can add videos directly on this page using the "Manage Content" toggle above, or through Admin Panel → Content/CMS → Guide Page Videos. Supports YouTube and Vimeo links.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
